import { makeAutoObservable, toJS } from 'mobx'
import { cartMobx, productCenter, productSaleMobx, storeMobx, restaurantCenter, RESTAURANT_EVENT, restaurantConfig } from 'restaurant'
import { deliveryMobx } from '@mobxStore'
import { each, uniq, sortBy, maxBy, pick, cloneDeep, debounce, some } from '@dt/fe-utils/dist/lodash'
import { ddayjs, format } from '@dt/fe-utils'
import { showMsg, time48To24 } from '@utils'
import ProductApi from '@api/product.api'
import StoreAPI from '@api/store.api'
import { TEEatType } from 'restaurant/mobx/store.mobx'

interface IPauseOrderTime {
  startTime: string;
  endTime: string;
}

const date = ddayjs().format('YYYY-MM-DD') // 当前日期

// 判断时间是否在营业时间
const timeInSale = (saleBeginEnds: IDay[], str: string) => {
  return some(saleBeginEnds, item => str >= item.begin && str <= item.end)
}

// 判断是否在停止接单时间内
const timeInPauseTime = (pauseOrderTime: IPauseOrderTime, time: any) => {
  return pauseOrderTime && time ? (ddayjs(time) as any).isBetween(ddayjs(pauseOrderTime.startTime), ddayjs(pauseOrderTime.endTime).add(1, 'ms')) : false
}

// 增加时间计算
const timeStr = (str: string, step: number) =>
  ddayjs(`${date} ${str}`, 'client').add(step, 'minutes')

// 减少时间计算
const timeSubtStr = (str: string, step: number) =>
  ddayjs(`${date} ${str}`, 'client').subtract(step, 'minutes')

// 判断是否在营业时间
const checkInSale = (saleBeginEnds: {
  begin: string;
  end: string;
}[], time: string) => {
  let nextTime = ''
  let isAcrossDay = false
  const inSale = !saleBeginEnds?.length || saleBeginEnds?.some((item, index) => {
    if (time > item.begin && time <= item.end) {
      if (item.end > '24:00') {
        isAcrossDay = true
      }
      return true
    }
    // 跨天判断
    if (item.end > '24:00') {
      if (time > '00:00' && time <= time48To24(item.end)) {
        isAcrossDay = true
        return true
      }
    }
    nextTime = saleBeginEnds[index]?.begin
    return false
  })
  return { inSale, nextTime, isAcrossDay }
}

// 默认时间间隔计算
const getDefaultTimeList = (intervalTime: number) => {
  const timeArr = [{
    timeStr: '00:00'
  }]
  const len = Math.floor(24 * (60 / intervalTime)) - 1
  for (let i = 1; i <= len; i++) {
    timeArr.push({
      timeStr: timeStr(timeArr[i - 1]?.timeStr, intervalTime).format('HH:mm')
    })
  }
  return timeArr
}

// 获取可预约的时间
// 规则：营业时间 + 餐品准备时间 作为起始时间，(intervalTime)分钟间隔, 直到营业间结束 + 餐品准备时间的时间
const getTimeList = (
  /** 间隔分钟 */
  intervalTime: number,
  /** 等待时间 */
  prepareTime: number = 0,
  /** 当天的最后营业时间 */
  saleBeginEnds: IDay[],
  /** 是今天 */
  isToday: boolean,
  /** 可预约当天 */
  canNow: boolean,
  /** 天数下标 以当前时间为0 */
  dayIndex: number,
  /** 临时闭店时间 */
  pauseOrderTime: IPauseOrderTime,
  /** 订单排队时长 */
  waitingTime: number = 0,
) => {
  let list: {
    timeStr: string;
  }[] = []
  // 总的等待时间 订单排队时长只在当天计算
  const allPrepareTime = prepareTime + (dayIndex === 0 ? waitingTime : 0)
  const _hour = Math.floor((allPrepareTime % (24 * 60)) / 60)
  const _prepareTime = allPrepareTime - (_hour * 60)
  const nowTime = ddayjs().format('HH:mm') // 当前时间
  const startTime = ddayjs().add(_hour, 'hour').format('HH:mm') // 起始时间
  const defaultTimeList = getDefaultTimeList(intervalTime)
  const inSale = !saleBeginEnds?.length || saleBeginEnds?.some((item) => {
    if (nowTime > item.begin && nowTime <= item.end) {
      return true
    }
    // 跨天判断
    if (item.end > '24:00') {
      if (nowTime > '00:00' && nowTime <= time48To24(item.end)) {
        return true
      }
    }
    return false
  })
  const _saleBeginEnds = cloneDeep(saleBeginEnds)?.map(item => {
    item.end = item.end > '24:00' ? '24:00' : item.end
    return item
  })
  const nowInPauseTime = timeInPauseTime(pauseOrderTime, ddayjs().format('YYYY-MM-DD HH:mm:ss'))

  _saleBeginEnds?.forEach((item) => {
    // 截止时间为营业结束时间 + 餐品准备时间，但是不能跨天
    // => 截止时间为营业结束时间 2022.06.06
    const endTime = item.end
    const ppHour = 60 * _hour || 0
    const ppTime = item.isAcrossDay ? 0 : _prepareTime

    // 可选时间为从0点开始，15分钟为间隔，并且在营业时间之内的时间
    list = list.concat(defaultTimeList.filter((timeItem) => {
      const _time = `${ddayjs().add(dayIndex, 'day').format('YYYY-MM-DD')} ${timeItem.timeStr}:00`
      if (timeInPauseTime(pauseOrderTime, _time)) {
        return false
      }
      if (ddayjs().add(allPrepareTime, 'minute').isAfter(_time)) {
        return false
      }
      if (timeItem.timeStr > endTime || timeItem.timeStr < timeStr(item.begin, ppHour + ppTime).format('HH:mm') || list.map(item => item.timeStr)?.includes(timeItem.timeStr)) {
        return false
      }
      // 今天设置时间必须超过起始时间 + _prepareTime + 3, 防止跨天
      // 2022.03.11 去掉 + 3
      if (isToday && (timeStr(nowTime, 0).format('HH:mm') >= timeItem.timeStr || timeStr(nowTime, 0).format('HH:mm') > timeStr(startTime, 0).format('HH:mm') || timeStr(startTime, ppTime).format('HH:mm') >= timeItem.timeStr || timeStr(startTime, ppTime).format('MM-DD') !== timeStr(timeItem.timeStr, 0).format('MM-DD'))) {
        return false
      }
      // 跨天了
      if (timeStr(timeItem.timeStr, 0).format('MM-DD') !== timeStr(startTime, 0).format('MM-DD')) {
        return false
      }
      return true
    }))

    if (isToday && list.length && inSale && canNow && !nowInPauseTime) {
      list.splice(0, 1, { timeStr: '' })
    }
  })

  return sortBy(list.filter(item => !item.timeStr || timeInSale(_saleBeginEnds, item.timeStr)), 'timeStr')
}

// 获取可预约时间间隔
const getTimeRangeList = (
  /** 时段 */
  pickTimeRange: any[],
  /** 等待时间 */
  prepareTime: number = 0,
  /** 当天的最后营业时间 */
  saleBeginEnds: IDay[],
  /** 是今天 */
  isToday: boolean,
  /** 天数下标 以当前时间为0 */
  dayIndex: number,
  /** 临时闭店时间 */
  pauseOrderTime: IPauseOrderTime,
  /** 订单排队时长 */
  waitingTime: number = 0,
) => {
  let list: {
    timeStr: string;
    pickTimeRangeStr: string;
    pickTimeRange: any;
  }[] = []
  // 总的等待时间
  const allPrepareTime = prepareTime + waitingTime
  const _hour = Math.floor((allPrepareTime % (24 * 60)) / 60)
  const _prepareTime = allPrepareTime - (_hour * 60)
  const nowTime = ddayjs().format('HH:mm') // 当前时间
  const startTime = ddayjs().add(_hour, 'hour').format('HH:mm') // 起始时间
  const timeRnageList = sortBy(pickTimeRange, 'start').map((item: any) => ({
    timeStr: item.start,
    pickTimeRangeStr: `${format.toTime(item.start, 'HH:mm')} - ${format.toTime(item.end, 'HH:mm')}`,
    pickTimeRange: item ? JSON.stringify(item) : ''
  }))
  const _saleBeginEnds = cloneDeep(saleBeginEnds)?.map(item => {
    item.end = item.end > '24:00' ? '24:00' : item.end
    return item
  })

  _saleBeginEnds?.forEach((item) => {
    // 截止时间为营业结束时间 + 餐品准备时间，但是不能跨天
    const endTime = item.end
    const ppHour = 60 * _hour || 0
    const ppTime = item.isAcrossDay ? 0 : _prepareTime

    // 可选时间为从0点开始，15分钟为间隔，并且在营业时间之内的时间
    list = list.concat(timeRnageList.filter((timeItem) => {
      const _time = `${ddayjs().add(dayIndex, 'day').format('YYYY-MM-DD')} ${timeItem.timeStr}:00`
      if (timeInPauseTime(pauseOrderTime, _time)) {
        return false
      }
      if (ddayjs().add(allPrepareTime, 'minute').isAfter(_time)) {
        return false
      }
      if (timeItem.timeStr > endTime || timeItem.timeStr < timeStr(item.begin, ppHour + ppTime).format('HH:mm') || list.map(item => item.timeStr)?.includes(timeItem.timeStr)) {
        return false
      }
      // 今天设置时间必须超过起始时间 + _prepareTime + 3, 防止跨天
      // 2022.03.11 去掉 + 3
      if (isToday && (timeStr(nowTime, 0).format('HH:mm') >= timeItem.timeStr || timeStr(nowTime, 0).format('HH:mm') > timeStr(startTime, 0).format('HH:mm') || timeStr(startTime, ppTime).format('HH:mm') >= timeItem.timeStr || timeStr(startTime, ppTime).format('MM-DD') !== timeStr(timeItem.timeStr, 0).format('MM-DD'))) {
        return false
      }
      // 跨天了
      if (timeStr(timeItem.timeStr, 0).format('MM-DD') !== timeStr(startTime, 0).format('MM-DD')) {
        return false
      }
      return true
    }))
  })

  return sortBy(list.filter(item => !item.timeStr || timeInSale(_saleBeginEnds, item.timeStr)), 'timeStr')
}

// 获取可预约时间数据
const getTimePickList = ({
  pauseOrderTime,
  bookingTime, // 预约时间
  advanceDay = 0, // 提前几天预约
  prepareTime = 0, // 餐品准备时间
  weeks, // 可取餐时间
  pickTimeRange, // 预约时间段
  waitingTime = 0,
  canNow,
  asapTime,
  callback,
}: {
  /** 暂停接单时间 */
  pauseOrderTime?: IPauseOrderTime;
  /** 预约时间 */
  bookingTime: number;
  /** 提前几天预约 */
  advanceDay?: number;
  /** 餐品准备时间 */
  prepareTime: number;
  /** 可取餐时间 */
  weeks: number[];
  /** 预约时间段 */
  pickTimeRange?: any[];
  /** 订单排队时长 */
  waitingTime?: number;
  /** 是否可以预约当天 */
  canNow?: boolean;
  /** 立即时间 */
  asapTime?: any;
  callback?: (timePickList: ITimeSelectList[]) => void;
}) => {
  let timePickList: ITimeSelectList[] = []
  const saleTime = cloneDeep(storeMobx.info?.saleTime) || [] // 售卖时间
  const nowTime = ddayjs().format('HH:mm') // 当前时间
  const nowDate = ddayjs().format('MM-DD') // 当前天
  const intervalTime = storeMobx.info?.bookingIntervalTime?.data || 15 // 时间间隔
  let list: ITimeSelectList[] = []
  let weekDay = '0'
  let date = ''
  /** 外卖配送时长 */
  let expressTime = cartMobx.info.eatType === 'delivery' ? deliveryMobx.expressTime || 0 : 0

  // 获取跨天营业时间
  const getAcrossDaySaleTime = (index: number) => {
    let wkday = ddayjs().add(index, 'day').format('d') === '0' ? '7' : ddayjs().add(index, 'day').format('d')
    let saleBeginEnds = saleTime.find((item) => String(item.day) === wkday)?.list
    if (saleBeginEnds?.length) {
      saleBeginEnds = sortBy(saleBeginEnds, 'begin')
    }
    let end = saleBeginEnds?.[saleBeginEnds?.length - 1]?.end
    // wkday可预约 && 跨天
    if (end > '24:00' && weeks.includes(Number(wkday))) {
      return [{
        begin: '00:00',
        end: time48To24(end),
        isAcrossDay: true
      }]
    }
    return []
  }

  // 缓存时间计算列表优化性能
  const cacheSaleBeginEndsMap = {}
  const cacheLastSaleBeginEndsMap = {}
  const cachePreSaleBeginEnds = {}

  // 根据最长可预约时间获取选择列表
  for (let i = advanceDay; i < bookingTime + advanceDay; i++) {
    weekDay = ddayjs().add(i, 'day').format('d') === '0' ? '7' : ddayjs().add(i, 'day').format('d')
    date = ddayjs().add(i, 'day').format('MM-DD')
    let isToday = nowDate === date
    /** 当天的最后营业时间 */
    let saleBeginEnds = saleTime.find((item) => String(item.day) === weekDay)?.list
    // 上一天的跨天营业时间
    let preSaleBeginEnds = getAcrossDaySaleTime(i - 1)
    if (preSaleBeginEnds?.length) {
      saleBeginEnds?.unshift(...preSaleBeginEnds)
    }
    // 营业时间排序
    if (saleBeginEnds?.length) {
      saleBeginEnds = sortBy(saleBeginEnds, 'begin')
    }
    // 需要在营业，对于当日需要点餐时在就餐时间内
    let end = saleBeginEnds?.[saleBeginEnds?.length - 1]?.end
    // 跨天营业时间
    if (end > '24:00') {
      end = '24:00'
    }
    if (!saleBeginEnds?.length || (end < nowTime && i === 0)) {
      continue
    }
    if (weeks?.includes(Number(weekDay))) {
      let sTimeList
      let sKey = JSON.stringify(toJS(saleBeginEnds))
      if (cacheSaleBeginEndsMap?.[sKey]) {
        sTimeList = cacheSaleBeginEndsMap[sKey]
      } else {
        sTimeList = pickTimeRange?.length ? getTimeRangeList(pickTimeRange, prepareTime + expressTime, saleBeginEnds, isToday, i, pauseOrderTime, waitingTime) : getTimeList(intervalTime, prepareTime, saleBeginEnds, isToday, canNow, i, pauseOrderTime, waitingTime)
        if (i !== advanceDay && i > (advanceDay + 1)) {
          cacheSaleBeginEndsMap[sKey] = sTimeList
        }
      }
      const items = [{
        date,
        dayIndex: i,
        timeList: sTimeList,
      }]
      // 最后一天跨天放到下一天
      if (i === bookingTime + advanceDay - 1) {
        let j = i + 1
        let lastSaleBeginEnds = getAcrossDaySaleTime(i)
        if (lastSaleBeginEnds?.length) {
          let lTimeList
          let lKey = JSON.stringify(toJS(lastSaleBeginEnds))
          if (cacheLastSaleBeginEndsMap?.[lKey]) {
            lTimeList = cacheLastSaleBeginEndsMap[lKey]
          } else {
            lTimeList = pickTimeRange?.length ? getTimeRangeList(pickTimeRange, 0, lastSaleBeginEnds, false, i, pauseOrderTime) : getTimeList(intervalTime, 0, lastSaleBeginEnds, false, false, i, pauseOrderTime)
            cacheLastSaleBeginEndsMap[lKey] = lTimeList
          }
          items.push({
            date: ddayjs().add(j, 'day').format('MM-DD'),
            dayIndex: j,
            timeList: lTimeList,
          })
        }
      }
      list.push(...items)
    } else if (preSaleBeginEnds?.length) {
      let pTimeList
      let pKey = JSON.stringify(toJS(preSaleBeginEnds))
      if (cachePreSaleBeginEnds?.[pKey]) {
        pTimeList = cloneDeep(cachePreSaleBeginEnds[pKey])
      } else {
        pTimeList = pickTimeRange?.length ? getTimeRangeList(pickTimeRange, 0, preSaleBeginEnds, i === advanceDay, i, pauseOrderTime) : getTimeList(intervalTime, 0, preSaleBeginEnds, i === advanceDay, canNow, i, pauseOrderTime)
        if (i !== advanceDay && i > (advanceDay + 1)) {
          cachePreSaleBeginEnds[pKey] = pTimeList
        }
      }
      const items = [{
        date,
        dayIndex: i,
        timeList: pTimeList,
      }]
      list.push(...items)
    }
    /** 当天不可预约, 立即时间可选 */
    if (isToday && canNow && !weeks?.includes(Number(weekDay))) {
      const nowInPauseTime = timeInPauseTime(pauseOrderTime, ddayjs().format('YYYY-MM-DD HH:mm:ss'))
      const inSale = storeMobx.info.inSale
      if (!nowInPauseTime && inSale) {
        const items = [{
          date,
          dayIndex: i,
          timeList: [{ timeStr: '' }],
        }]
        list.push(...items)
      }
    }
  }

  // 自营外卖送达时间
  if (!pickTimeRange?.length && cartMobx.info.eatType === 'delivery') {
    let newList = []
    // 当前时间 + 配送时间 + 餐品制作时间
    const nowTime = timeStr(ddayjs().format('HH:mm'), deliveryMobx.expressTime + prepareTime).format('YYYYMMDDHHmm')
    const nextMap = {}
    const hasToday = list[0]?.dayIndex === 0
    const _index = Number(ddayjs(asapTime.time).format('YYYYMMDD') || '') - Number(ddayjs().format('YYYYMMDD') || '')
    each(list, (item, index) => {
      // item.dayIndex = _index > 0 ? item.dayIndex + _index : item.dayIndex
      const timeList = item.timeList.map(t => {
        return {
          timeStr: t.timeStr ? timeStr(t.timeStr, deliveryMobx.expressTime).format('HH:mm') : t.timeStr
        }
      })
      const max = maxBy(timeList, 'timeStr')
      const idx = timeList.findIndex(item => item.timeStr === max.timeStr)
      let newTL = idx > -1 ? timeList.slice(0, idx + 1) : timeList
      if (item.dayIndex === 0) {
        newTL = newTL.filter(time => !time.timeStr || time.timeStr > ddayjs().format('HH:mm'))
      }
      if (nextMap[item.dayIndex]) {
        newTL.unshift(...nextMap[item.dayIndex])
      }
      // 当天的最迟的时间，用于和当天立即时间比较
      const maxTime = !!max?.timeStr && timeStr(max.timeStr, 0).format('YYYYMMDDHHmm')
      if (item.dayIndex === 0 && newTL.length === 1 && newTL?.[0]?.timeStr === '' && (!!max?.timeStr && nowTime >= maxTime)) {
        // 如果当前时段只有立即时间，且立即时间超过营业时间，就不要这个时段
      } else {
        const cItem = {
          date: _index > 0 ? ddayjs().add(item.dayIndex, 'day').format('MM-DD') : item.date,
          dayIndex: item.dayIndex,
          timeList: uniq(newTL)
        }
        newList.push(cItem)
      }
      if (idx > -1) {
        const nextTL = timeList.slice(idx + 1, timeList.length)
        const dayIndex = hasToday ? item.dayIndex + 1 : item.dayIndex
        nextMap[dayIndex] = nextTL
        if (index === list.length - 1 && nextMap[item.dayIndex + 1]) {
          newList.push({
            date: ddayjs().add(item.dayIndex + 1, 'day').format('MM-DD'),
            dayIndex: item.dayIndex + 1,
            timeList: nextMap[item.dayIndex + 1]
          })
        }
      }
    })
    timePickList = newList.filter((item) => item.timeList.length)
  } else {
    timePickList = list.filter((item) => item.timeList.length)
  }

  // callback
  callback && callback(timePickList)

  return timePickList
}

// 获取立即取餐时间
const getAsapTime = ({
  pauseOrderTime,
  prepareTime = 0,
  expressTime,
  nextTime,
  waitingTime = 0,
}: {
  pauseOrderTime?: IPauseOrderTime;
  prepareTime: number;
  expressTime?: number;
  nextTime: string;
  waitingTime?: number;
}) => {
  const nowTime = ddayjs().format('HH:mm') // 当前时间
  const intervalTime = storeMobx.info?.bookingIntervalTime?.data || 15 // 时间间隔
  const saleTime = cloneDeep(storeMobx.info?.saleTime) || [] // 售卖时间
  const weekDay = ddayjs().format('d') === '0' ? '7' : ddayjs().format('d')
  const saleBeginEnds = saleTime.find((item) => String(item.day) === weekDay)?.list
  let { inSale, isAcrossDay } = checkInSale(saleBeginEnds, nowTime)
  let _asapTime
  if (inSale) {
    _asapTime = timeStr(nowTime, prepareTime + (expressTime || 0) + waitingTime)
    const _time: any = timeStr(nowTime, prepareTime)
    const { inSale: asapInSale, nextTime: asapNextTime, isAcrossDay: asapIsAcrossDay } = checkInSale(saleBeginEnds, ddayjs(_time).format('HH:mm'))
    if (!asapInSale) {
      isAcrossDay = asapIsAcrossDay
      _asapTime = timeStr(asapNextTime, prepareTime + (expressTime || 0) + waitingTime)
    }
  } else if (nextTime) {
    _asapTime = timeSubtStr(nextTime, intervalTime + (expressTime || 0) + waitingTime)
  }
  if (isAcrossDay) {
    _asapTime = timeStr(ddayjs(_asapTime).format('HH:mm'), 0)
  }
  if (timeInPauseTime(pauseOrderTime, _asapTime)) {
    _asapTime = 0
  }
  return {
    time: _asapTime ? _asapTime.valueOf() : 0,
    timeStr: _asapTime ? format.toTime(_asapTime, 'HH:mm') : ''
  }
}

// 获取餐品预约购物车信息
const getProductReserveCartInfo = (info: ITimePickInfo) => {
  const day = ddayjs().add(info.pickDayIndex, 'day').format('YYYY-MM-DD')
  const startTime = info.pickTime ? `${day} ${info.pickTime}:00` : ''
  let pickTimeRange = info.pickTimeRange
  if (pickTimeRange) {
    try {
      pickTimeRange = JSON.parse(pickTimeRange) || {}
      if (pickTimeRange.start) {
        pickTimeRange.start = `${day} ${pickTimeRange.start}:00`
        pickTimeRange.end = `${day} ${pickTimeRange.end}:00`
        pickTimeRange = JSON.stringify(pickTimeRange)
      }
    } catch (error) {
    }
  }
  return { startTime, pickTimeRange }
}

class TimePickMobx {
  constructor () {
    makeAutoObservable(this, {}, { autoBind: true })

    // 购物车餐品预约时间、排序
    const _resetCartItems = debounce(() => {
      this.resetCartItems()
    }, 500)

    // 监听就餐方式、配送时间
    restaurantCenter.on([RESTAURANT_EVENT.EAT_TYPE_CHANGE, RESTAURANT_EVENT.CART_EXPRESS_TIME_CHANGED], () => {
      _resetCartItems()
    })

    // 监听门店变化
    restaurantCenter.on(RESTAURANT_EVENT.STORE_CHANGED, () => {
      // 初始化餐品预约时间配置
      this.getProductRule()
    })
  }

  canNow: boolean = false

  timePickList: ITimeSelectList[] = []

  selectedInfo: ITimePickInfo = { pickDayIndex: 0, pickTime: '' }

  productNo: string = ''

  productRuleMap: {
    [key: string]: IProductReserveRule;
  } = {}

  productCanNow: boolean = false

  productTimePickList: ITimeSelectList[] = []

  productSelectedInfo: ITimePickInfo

  /** 是否可以预约 */
  get canBookingTime () {
    if (!storeMobx.info?.isMobileBookingOrder) {
      return false
    }
    /** 堂食送餐制，不需要预约 */
    if (Config.codeType === 'brand' && cartMobx.info.eatType === TEEatType.dinein && storeMobx.storeSceneConfig?.diningMode === 2) {
      return false
    }
    return true
  }

  /** 暂时关闭线上点餐 */
  get pauseOrderTime () {
    // if (Config.codeType !== 'brand') {
    //   return null
    // }
    const { eatType } = cartMobx.info
    const { pauseMobileOrderTime, pauseSelfDeliveryOrderTime, pauseReserveOrderTime, pausePickupOrderTime } = storeMobx.info
    if (pauseMobileOrderTime?.all || pauseMobileOrderTime?.togo) {
      return pick(pauseMobileOrderTime, ['startTime', 'endTime']) as IPauseOrderTime
    }
    if (pauseReserveOrderTime?.status && eatType === 'dinein') {
      return pick(pauseReserveOrderTime, ['startTime', 'endTime']) as IPauseOrderTime
    }
    if (pauseSelfDeliveryOrderTime?.status && eatType === 'delivery') {
      return pick(pauseSelfDeliveryOrderTime, ['startTime', 'endTime']) as IPauseOrderTime
    }
    if (pausePickupOrderTime?.status && eatType === 'pickup') {
      return pick(pausePickupOrderTime, ['startTime', 'endTime']) as IPauseOrderTime
    }
  }

  get canPick () {
    return this.timePickList.length && this.timePickList.some((item: ITimeSelectList) => item.timeList.length)
  }

  get asapTime () {
    const expressTime = cartMobx.info?.eatType === 'delivery' ? deliveryMobx.expressTime || 0 : 0
    return getAsapTime({
      pauseOrderTime: this.pauseOrderTime,
      prepareTime: storeMobx.info?.preparationTime?.[0]?.time?.data === 0 ? 0 : (storeMobx.info?.preparationTime?.[0]?.time?.data || 0),
      expressTime,
      nextTime: this.timePickList[0]?.timeList[1]?.timeStr,
      waitingTime: storeMobx.info?.mealsWaitingTime?.waitingTime || 0,
    })
  }

  get productAsapTime () {
    const expressTime = cartMobx.info?.eatType === 'delivery' ? deliveryMobx.expressTime || 0 : 0
    return getAsapTime({
      pauseOrderTime: this.pauseOrderTime,
      prepareTime: this.productRuleMap?.[this.productNo]?.preparationTime,
      expressTime,
      nextTime: this.productTimePickList[0]?.timeList[1]?.timeStr,
      waitingTime: storeMobx.info?.mealsWaitingTime?.waitingTime || 0,
    })
  }

  get productReserveInfo () {
    return this.productSelectedInfo ? getProductReserveCartInfo(this.productSelectedInfo) : null
  }

  get notOrderPick () {
    return cartMobx.viewCartItems?.every(cartItem => !!cartItem?.startTime)
  }

  init () {
    if (!this.canBookingTime) return
    // 初始化门店预约时间
    this.timePickList = getTimePickList({
      pauseOrderTime: this.pauseOrderTime,
      bookingTime: storeMobx.info?.longBookingTime?.data || 1,
      prepareTime: storeMobx.info?.preparationTime?.[0]?.time?.data === 0 ? 0 : (storeMobx.info?.preparationTime?.[0]?.time?.data || 0),
      weeks: storeMobx.info?.bookingWeeks,
      waitingTime: storeMobx.info?.mealsWaitingTime?.waitingTime || 0,
      canNow: true,
      asapTime: this.asapTime,
      callback: (timePickList) => {
        this.setCanNow(!timePickList?.[0]?.timeList[0]?.timeStr)
      }
    })
  }

  reset (noReload?: boolean) {
    this.init()
    const time = this.timePickList[0]
    const pickTime = Config.codeType === 'brand' ? (this.canNow ? '' : time?.timeList[0]?.timeStr) || '' : ''
    const pickDayIndex = Config.codeType === 'brand' ? (this.canNow ? 0 : time?.dayIndex) || 0 : 0
    const pickDay = Config.codeType === 'brand' ? (this.canNow ? '' : time?.date) || '' : ''
    this.setSelectedInfo({ pickDayIndex, pickTime, pickDay }, !noReload)
  }

  /** 验证立即时间排队等待时间是否偏差10分钟左右 +- 10min */
  async checkWaitingTimeToAspaTime () {
    // 只在手机验证 开启预约 订单级别立即时间 开启kds排队
    if (restaurantConfig.app === 'mobile' && storeMobx.info.isMobileBookingOrder && cartMobx.info.asapTime && storeMobx.info.waitingTimeConfig?.status) {
      /** 校验当前等待时间 */
      const preWaitingTime = storeMobx.info.mealsWaitingTime?.waitingTime || 0
      try {
        const mealsWaiting = await StoreAPI.getMealsWaitingTime(storeMobx.info.storeId)
        /** 验证当前排队时间和接口返回的是否偏差10分钟 */
        if (Math.abs(preWaitingTime - mealsWaiting.waitingTime) > 10) {
          storeMobx.setWaitingTime({ mealsWaiting })
          showMsg(i18n.t(`newCart.${cartMobx.info.eatType}TimeReset`), 3000)
          return true
        }
      } catch (error) {
      }
    }
    return false
  }

  setSelectedInfo (info: ITimePickInfo, reloadMenu?: boolean) {
    this.selectedInfo = info
    const appointmentDate = productCenter.appointmentDate
    const appointmentTime = productCenter.appointmentTime
    const prepareTime = storeMobx.info?.preparationTime?.[0]?.time?.data === 0 ? 0 : (storeMobx.info?.preparationTime?.[0]?.time?.data || 0) // 餐品准备时间
    const expressTime = cartMobx.info?.eatType === 'delivery' ? deliveryMobx.expressTime || 0 : 0 // 外卖配送时间
    const pickTime = storeMobx.info?.isMobileBookingOrder && this.canPick ? timeSubtStr(info.pickTime || ddayjs(this.asapTime.time, 'client').format('HH:mm'), prepareTime + expressTime).format('HH:mm') : ''
    const day = ddayjs().add(info.pickDayIndex, 'day').format('YYYY-MM-DD')
    const appointmentDay = day === ddayjs().format('YYYY-MM-DD') ? undefined : day
    const isChangeDate = appointmentDay !== appointmentDate
    const isChangeTime = pickTime !== appointmentTime
    const startTime = info.pickTime ? `${day} ${info.pickTime}:00` : ''
    const menuTime = startTime ? ddayjs(`${startTime}`, 'client').subtract(prepareTime + expressTime, 'minutes').format('YYYY-MM-DD HH:mm:ss') : ''
    // startTime 为空需要填写立即取餐时间
    if (startTime === '' && this.canPick) {
      cartMobx.setAsapTime(ddayjs(this.asapTime.time).format('YYYY-MM-DD HH:mm:ss'))
    }
    cartMobx.setStartTime(startTime)
    cartMobx.setMenuTime(menuTime)
    productCenter.setAppointmentDate(appointmentDay, () => {
      if (isChangeDate) {
        // productSaleMobx.init() // 修改预约时间会更新菜单，更新菜单本身就会触发库存接口
      }
    })
    productCenter.setAppointmentTime(pickTime, (isInit) => {
      // 刷新菜单
      if (isChangeTime && (reloadMenu && !isInit)) {
        // 延迟触发，和就餐方式切换错开
        setTimeout(() => {
          productCenter.setStore(storeMobx.info.brandId, storeMobx.info.storeId)
          productCenter.initProduct()
        }, 100)
      }
    })
  }

  setCanNow (canNow: boolean) {
    this.canNow = canNow
  }

  initProduct (no: string, startTime?: string, pickTimeRange?: string) {
    this.productNo = ''
    if (!this.canBookingTime) return
    if (!this.productRuleMap?.[no]) return
    this.productNo = no
    this.productTimePickList = getTimePickList({
      pauseOrderTime: this.pauseOrderTime,
      bookingTime: this.productRuleMap?.[no]?.earlyDay,
      advanceDay: this.productRuleMap?.[no]?.advanceDay,
      prepareTime: this.productRuleMap?.[no]?.preparationTime,
      weeks: this.productRuleMap?.[no]?.week,
      pickTimeRange: this.productRuleMap?.[no]?.pickTimeRange,
      waitingTime: storeMobx.info?.mealsWaitingTime?.waitingTime || 0,
      canNow: false,
      asapTime: this.asapTime,
      callback: (timePickList) => {
        this.setProductCanNow(!timePickList?.[0]?.timeList[0]?.timeStr)
      }
    })
    this.resetProduct()
    if (startTime) {
      const pickDay = ddayjs(startTime).format('MM-DD')
      const pickTime = ddayjs(startTime).format('HH:mm')
      const pickDayIndex = this.productTimePickList.find(item => item.date === pickDay)?.dayIndex
      if (pickTimeRange) {
        try {
          const pickTimeRangeObj = JSON.parse(pickTimeRange) || {}
          if (pickTimeRangeObj.start) {
            pickTimeRangeObj.start = ddayjs(pickTimeRangeObj.start).format('HH:mm')
            pickTimeRangeObj.end = ddayjs(pickTimeRangeObj.end).format('HH:mm')
            pickTimeRange = JSON.stringify(pickTimeRangeObj)
          }
        } catch (error) {
        }
      }
      this.setProductSelectedInfo({
        pickDayIndex,
        pickDay,
        pickTime,
        pickTimeRange
      })
    }
  }

  resetProduct () {
    const time = this.productTimePickList[0]
    const pickTime = this.productCanNow ? '' : time?.timeList[0]?.timeStr
    const pickDayIndex = this.productCanNow ? 0 : time?.dayIndex
    const pickDay = this.productCanNow ? '' : time?.date
    const pickTimeRange = this.productCanNow ? '' : time?.timeList[0]?.pickTimeRange
    this.setProductSelectedInfo({ pickDayIndex, pickTime, pickDay, pickTimeRange })
  }

  setProductSelectedInfo (info: ITimePickInfo) {
    this.productSelectedInfo = info
  }

  setProductCanNow (canNow: boolean) {
    this.productCanNow = canNow
  }

  async getProductRule () {
    this.productRuleMap = {}
    if (!storeMobx.info?.isMobileBookingOrder) return
    const list = await ProductApi.getProductReserveRule()
    if (!list?.error) {
      each(list, item => {
        const obj = pick(item, ['earlyDay', 'preparationTime']) as IProductReserveRule
        const preparationDay = Math.floor(obj.preparationTime / (24 * 60)) || 0
        try {
          obj.week = item.week ? JSON.parse(item.week) : []
          obj.pickTimeRange = item.pickTimeRange ? JSON.parse(item.pickTimeRange) : []
        } catch (error) {
        }
        obj.advanceDay = item.advanceDay || preparationDay || 0
        obj.preparationTime = item.preparationTime - preparationDay * 24 * 60
        each(item.productNos, p => {
          this.productRuleMap[p] = obj
        })
      })
    }
  }

  resetCartItems (sort?: boolean) {
    if (Config.codeType === 'brand' && storeMobx.info?.isMobileBookingOrder && cartMobx.cartItems?.length) {
      let reset = false
      let nCartItems = []
      cartMobx.cartItems.forEach((cartItem: ICartItem) => {
        const obj = cloneDeep(cartItem)
        const { productNo } = obj
        let _remove = false
        if (this.productRuleMap[productNo]) {
          reset = true
          const _timePickList = getTimePickList({
            pauseOrderTime: this.pauseOrderTime,
            bookingTime: this.productRuleMap?.[productNo]?.earlyDay,
            advanceDay: this.productRuleMap?.[productNo]?.advanceDay,
            prepareTime: this.productRuleMap?.[productNo]?.preparationTime,
            weeks: this.productRuleMap?.[productNo]?.week,
            pickTimeRange: this.productRuleMap?.[productNo]?.pickTimeRange,
            canNow: false,
            asapTime: this.asapTime,
          })
          const _info = {
            pickDayIndex: _timePickList?.[0]?.dayIndex,
            pickDay: _timePickList?.[0]?.date,
            pickTime: _timePickList?.[0]?.timeList[0]?.timeStr,
            pickTimeRange: _timePickList?.[0]?.timeList[0]?.pickTimeRange
          }
          // 删除不能预约餐品
          if (!_timePickList?.length) {
            _remove = true
          }
          if (!_remove) {
            // 重置餐品预约时间
            const { startTime, pickTimeRange } = getProductReserveCartInfo(_info)
            if (!cartItem.startTime || ddayjs(cartItem.startTime).valueOf() < ddayjs(startTime).valueOf()) {
              obj.startTime = startTime
              obj.pickTimeRange = pickTimeRange
            }
            // 重置餐品预约时间(满送餐品)
            obj.fullItemNFreeProducts?.forEach(item => {
              item.startTime = obj.startTime
              item.pickTimeRange = obj.pickTimeRange
            })
            // 重置套餐子餐品预约时间
            obj.productGroups?.forEach(group => {
              group.productGroupDetails?.forEach(item => {
                item.startTime = obj.startTime
                item.pickTimeRange = obj.pickTimeRange
              })
            })
          }
        } else {
          if (obj.startTime || obj.pickTimeRange) {
            reset = true
            delete obj.startTime
            delete obj.pickTimeRange
            obj.fullItemNFreeProducts?.forEach(item => {
              delete item.startTime
              delete item.pickTimeRange
            })
            obj.productGroups?.forEach(group => {
              group.productGroupDetails?.forEach(item => {
                delete item.startTime
                delete item.pickTimeRange
              })
            })
          }
        }
        // 换购品
        if (!_remove && obj.swapBuyProducts?.length) {
          const swapBuyProducts = []
          obj.swapBuyProducts.forEach(item => {
            const _obj = cloneDeep(item)
            const { productNo: swProductNo } = item
            if (this.productRuleMap[swProductNo]) {
              reset = true
              const _swTimePickList = getTimePickList({
                pauseOrderTime: this.pauseOrderTime,
                bookingTime: this.productRuleMap?.[swProductNo]?.earlyDay,
                advanceDay: this.productRuleMap?.[swProductNo]?.advanceDay,
                prepareTime: this.productRuleMap?.[swProductNo]?.preparationTime,
                weeks: this.productRuleMap?.[swProductNo]?.week,
                pickTimeRange: this.productRuleMap?.[swProductNo]?.pickTimeRange,
                canNow: false,
                asapTime: this.asapTime,
              })
              const _swInfo = {
                pickDayIndex: _swTimePickList?.[0]?.dayIndex,
                pickDay: _swTimePickList?.[0]?.date,
                pickTime: _swTimePickList?.[0]?.timeList[0]?.timeStr,
                pickTimeRange: _swTimePickList?.[0]?.timeList[0]?.pickTimeRange
              }
              if (_swTimePickList?.length) {
                // 重置餐品预约时间
                const { startTime, pickTimeRange } = getProductReserveCartInfo(_swInfo)
                if (!item.startTime || ddayjs(item.startTime).valueOf() < ddayjs(startTime).valueOf()) {
                  _obj.startTime = startTime
                  _obj.pickTimeRange = pickTimeRange
                }
                swapBuyProducts.push(_obj)
              }
            } else {
              delete _obj.startTime
              delete _obj.pickTimeRange
              swapBuyProducts.push(_obj)
            }
          })
          obj.swapBuyProducts = swapBuyProducts
        }
        if (!_remove) {
          nCartItems.push(obj)
        }
      })
      if (!reset) {
        return
      }
      if (sort) {
        nCartItems = sortBy(nCartItems, item => item?.startTime ? 1 : 0)
      }
      cartMobx.setCartItems(nCartItems, true)
    }
  }

  getTimeText (type: 'default' | 'order' | 'product', info?: { startTime?: string; pickTimeRange?: string }) {
    const { startTime, pickTimeRange } = info || {}
    if (type === 'default' || type === 'order') {
      if (type === 'default') {
        if (!this.selectedInfo?.pickTime) {
          return ''
        }
      }
      if (type === 'order') {
        if (cartMobx.info.eatType === 'delivery' && !deliveryMobx.address) {
          return i18n.t('newCart.selectAddressTip')
        }
        if (!this.selectedInfo?.pickTime) {
          const index = Number(ddayjs(this.asapTime.time).format('YYYYMMDD') || '') - Number(ddayjs().format('YYYYMMDD') || '')
          if (index) {
            const date = index >= 1 ? `${index === 1 ? i18n.t('checkout.tomorrow') : format.toDate(this.asapTime.time)}` : ''
            return `${date} ${this.asapTime.timeStr}`
          } else {
            return this.asapTime.timeStr
          }
        }
      }
      const index = this.selectedInfo?.pickDayIndex
      const date = index >= 1 ? `${index === 1 ? i18n.t('checkout.tomorrow') : format.toDate(`${new Date().getFullYear()}-${this.selectedInfo?.pickDay}`)}` : ''
      return `${date} ${format.toTime(this.selectedInfo?.pickTime, 'HH:mm')}`
    } else if (type === 'product') {
      const index = Number(ddayjs(startTime).format('YYYYMMDD') || '') - Number(ddayjs().format('YYYYMMDD') || '')
      const date = index >= 1 ? `${index === 1 ? i18n.t('checkout.tomorrow') : format.toDate(startTime)}` : ''
      if (pickTimeRange) {
        try {
          const pickTimeRangeObj = JSON.parse(pickTimeRange) || {}
          if (pickTimeRangeObj.start) {
            const start = ddayjs(pickTimeRangeObj.start).valueOf()
            const end = ddayjs(pickTimeRangeObj.end).valueOf()
            return `${date} ${format.toTime(start)}-${format.toTime(end)}`
          }
        } catch (error) {
        }
      }
      return `${date} ${format.toTime(ddayjs(startTime).valueOf())}`
    }
  }
}

export default new TimePickMobx()
